import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { createCustomStateSetOnUrlHandler, createStateResetHandler } from '@appState/helpers/state-reset-handler';
import { addEntity, deleteEntity, getAll, getOne, StateDictionary, updateEntity } from '@appState/helpers/store-base';
import { ConfiguredAlert, ConfiguredAlertListItem, CreateConfiguredAlertRequest, UpdateConfiguredAlertRequest } from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { ConfiguredAlertsService } from './configured-alerts.service';

interface ConfiguredAlertsState {
  configuredAlerts: ConfiguredAlertListItem[];
  selectedConfiguredAlert: ConfiguredAlert | null;
  isLoading: boolean;
  isConfiguredAlertCreateLoading: boolean;
  isSelectedConfiguredAlertLoading: boolean;
}

const initialState: ConfiguredAlertsState = {
  configuredAlerts: [],
  selectedConfiguredAlert: null,
  isLoading: false,
  isConfiguredAlertCreateLoading: false,
  isSelectedConfiguredAlertLoading: false,
};

const configuredAlertsDictionary: StateDictionary<ConfiguredAlertListItem> = {
  entityList: 'configuredAlerts',
  listLoading: 'isLoading',
  createLoading: 'isConfiguredAlertCreateLoading',
  selectedEntityLoading: 'isSelectedConfiguredAlertLoading',
  selectedEntity: 'selectedConfiguredAlert',
  entityId: 'id',
  sortFn: (a: ConfiguredAlertListItem, b: ConfiguredAlertListItem) => a.name.localeCompare(b.name),
};

export const ConfiguredAlertsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('configured-alerts-store'),
  withComputed(state => ({
    selectedConfiguredAlertId: computed(() => state.selectedConfiguredAlert()?.id ?? null),
  })),
  withMethods((store, configuredAlertsService = inject(ConfiguredAlertsService), router = inject(Router)) => {
    createStateResetHandler<ConfiguredAlertsState>('/monitoring/configured-alerts')(store, initialState);
    createCustomStateSetOnUrlHandler<ConfiguredAlertsState>('/monitoring/configured-alerts', {
      selectedConfiguredAlert: null,
      isSelectedConfiguredAlertLoading: false,
    })(store);

    const subscribeToAlert = rxMethod<{ id: string; isSubscribed: boolean }>(
      pipe(
        switchMap(({ id, isSubscribed }) => {
          patchState(store, { isSelectedConfiguredAlertLoading: true, isLoading: true });
          const delta = isSubscribed ? -1 : 1;
          return configuredAlertsService.subscribe(id).pipe(
            tap(() => {
              const alerts = store.configuredAlerts();
              const updatedAlerts = alerts.map((a: ConfiguredAlertListItem) =>
                a.id === id ? { ...a, subscribersCount: a.subscribersCount + delta } : a,
              );
              const selected = store.selectedConfiguredAlert();
              const updatedSelected =
                selected && selected.id === id
                  ? { ...selected, isSubscribed: !isSubscribed, subscribersCount: selected.subscribersCount + delta }
                  : selected;
              patchState(store, {
                configuredAlerts: updatedAlerts,
                selectedConfiguredAlert: updatedSelected,
                isSelectedConfiguredAlertLoading: false,
                isLoading: false,
              });
            }),
            catchError(() => EMPTY),
          );
        }),
      ),
    );

    const repositoryMethods = {
      getAll: getAll<ConfiguredAlertListItem>({
        store,
        service: configuredAlertsService,
        dictionary: configuredAlertsDictionary,
      }),

      getOne: getOne<ConfiguredAlert>({
        store,
        service: configuredAlertsService,
        dictionary: configuredAlertsDictionary,
      }),

      create: addEntity<ConfiguredAlert, CreateConfiguredAlertRequest>({
        store,
        service: configuredAlertsService,
        dictionary: configuredAlertsDictionary,
        onSuccessCallback: (created: ConfiguredAlert) => {
          patchState(store, { selectedConfiguredAlert: created });
          router.navigate(['/monitoring', 'configured-alerts', created.id, 'details']);
        },
      }),

      update: updateEntity<ConfiguredAlert, UpdateConfiguredAlertRequest>({
        store,
        service: configuredAlertsService,
        dictionary: configuredAlertsDictionary,
      }),

      delete: deleteEntity<ConfiguredAlert>({
        store,
        service: { delete: id => configuredAlertsService.delete(id, store.selectedConfiguredAlert()!.alertTypeId) },
        dictionary: configuredAlertsDictionary,
        onSuccessCallback: () => {
          router.navigate(['/monitoring', 'configured-alerts']);
        },
      }),

      subscribe: subscribeToAlert,
    };

    const navigationActions = {
      goToActions: async (id: string): Promise<boolean> => {
        return router.navigate(['monitoring', 'configured-alerts', id]);
      },
    };

    return { ...repositoryMethods, ...navigationActions };
  }),
);

export interface ConfiguredAlertsStoreType {
  readonly configuredAlerts: Signal<ConfiguredAlertListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly isConfiguredAlertCreateLoading: Signal<boolean>;
  readonly selectedConfiguredAlert: Signal<ConfiguredAlert | null>;
  readonly selectedConfiguredAlertId: Signal<string | null>;
  readonly isSelectedConfiguredAlertLoading: Signal<boolean>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly goToActions: (id: string) => Promise<boolean>;
  readonly create: (dto: CreateConfiguredAlertRequest) => void;
  readonly update: (input: { id: string; dto: UpdateConfiguredAlertRequest }) => void;
  readonly delete: (id: string) => void;
  readonly subscribe: (input: { id: string; isSubscribed: boolean }) => void;
}
